import { type FunctionComponent, useEffect, useState } from 'react';

import { Button, CustomDropdown, ThemeProvider } from '@dr.pogodin/react-utils';
import { captureException } from '@sentry/react';

import Throbber from 'components/Throbber';

import { updateUser } from 'services/user';

import {
  PAYMENT_METHOD_OPTIONS,
  type PaymentMethodT,
  getId,
  useGlobalState,
} from 'utils';

import dropdownTheme from 'themes/CustomDropdown/admin/with-inner-label-60px.scss';

import S from './style.scss';

type PropsT = {
  editable: true;
  onUpdate?: (update: {
    paymentMethod?: PaymentMethodT | null;
  }) => Promise<void>;
  paymentMethod?: PaymentMethodT | null;
} | {
  editable?: false;
  paymentMethod?: PaymentMethodT | null;
};

export const Invoicing: FunctionComponent<PropsT> = ({
  paymentMethod: propsPaymentMethod,
  ...props
}) => {
  const editable = 'editable' in props ? props.editable : false;
  const onUpdate = 'onUpdate' in props ? props.onUpdate : undefined;

  const [user, setUser] = useGlobalState('user');
  if (editable && !user && !('paymentMethod' in props)) {
    throw Error('Not authenticated');
  }

  const [busy, setBusy] = useState(false);

  const currentPaymentMethod = propsPaymentMethod
    ?? (editable ? user?.paymentMethod : undefined);

  const [
    paymentMethod,
    setPaymentMethod,
  ] = useState(currentPaymentMethod ?? undefined);

  const dirty = paymentMethod !== currentPaymentMethod;

  const THROBBER_HIDE_DELAY = 1000;
  const [showThrobber, setShowThrobber] = useState(busy);
  if (busy && !showThrobber) setShowThrobber(true);

  useEffect(() => {
    if (busy) return undefined;
    const id = setTimeout(() => {
      setShowThrobber(false);
    }, THROBBER_HIDE_DELAY);
    return () => {
      clearTimeout(id);
    };
  }, [busy]);

  return (
    <ThemeProvider themes={{ CustomDropdown: dropdownTheme }}>
      <div className={S.container}>
        {showThrobber ? (
          <div styleName="savingOverlay">
            <div styleName="savingOverlayInner">
              <Throbber />
            </div>
          </div>
        ) : null}

        {/* Payment Method Section */}
        <div styleName="section">
          <div styleName="sectionTitle">
            Payment Method
          </div>

          <div styleName="sectionContent">
            <CustomDropdown
              label="Payment Method"
              onChange={(value) => {
                const pm = (value || null) as PaymentMethodT | null;
                setPaymentMethod(pm ?? undefined);
              }}
              options={PAYMENT_METHOD_OPTIONS}
              value={paymentMethod}
            />
          </div>

          <div styleName="footer">
            <Button
              disabled={!dirty || busy}
              onClick={() => {
                void (async () => {
                  try {
                    setBusy(true);
                    if (onUpdate) {
                      await onUpdate({ paymentMethod: paymentMethod ?? null });
                    } else if (user) {
                      const userId = getId(user);
                      const res = await updateUser(userId, {
                        paymentMethod: paymentMethod ?? null,
                      });
                      setUser(res);
                    }
                  } catch (error) {
                    // eslint-disable-next-line no-alert
                    alert('Failed to save the changes');
                    captureException(error);
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};
